/**
 * keys.ts — KEYS-01 (Seth 2026-09-22: "when you get familiar with a program
 * you can sort of tab through and quick enter fields you know … anywhere on
 * pegasus desktop that could benefit from that please do").
 *
 * The office learned data entry on ESC, a Windows program where Enter moves to
 * the next field and a save is a deliberate key. Web forms default to "Enter
 * submits", which on a long entry form (a purchase order that hands a PO
 * number to a vendor) is an accident waiting to happen. So Pegasus's long
 * entry forms follow ESC's habit, and the short dialogs keep the web's:
 *
 *   · Tab / Shift+Tab — next / previous field, in reading order, skipping
 *     read-only fields and row trash cans (those get tabIndex -1)
 *   · Enter — the NEXT field on a long entry form (same as Tab); inside a
 *     list that is open, Enter PICKS the highlighted row
 *   · Ctrl+Enter (⌘+Enter) — SAVE from anywhere on the form, textareas included
 *   · Esc — close the list if one is open, else close the window
 *   · ↑ ↓ — move the highlight in any pick-list; typing filters it
 *
 * One helper for each piece, so every form behaves the same.
 */
import { useCallback, useEffect, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";

const FIELD_SELECTOR =
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]):not([readonly]), [role="combobox"]:not([disabled]), [data-field]';

function isVisible(el: HTMLElement): boolean {
  if (el.tabIndex < 0) return false;
  if (el.getAttribute("aria-hidden") === "true") return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** Every field a person would tab through inside `root`, in DOM (= reading) order. */
export function entryFields(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FIELD_SELECTOR)).filter(isVisible);
}

/** Move focus to the next (dir 1) or previous (dir -1) field after `from`.
 *  Text gets selected on arrival so typing replaces it — the quick-entry feel. */
export function focusNextField(from: HTMLElement, root: HTMLElement, dir: 1 | -1 = 1): boolean {
  const fields = entryFields(root);
  const i = fields.indexOf(from);
  const next = fields[i + dir];
  if (!next) return false;
  next.focus();
  if (next instanceof HTMLInputElement && (next.type === "text" || next.type === "number" || next.type === "search" || next.type === "tel" || next.type === "email")) {
    try { next.select(); } catch { /* number inputs in some browsers */ }
  }
  return true;
}

/**
 * Key handler for a LONG entry form's container: Enter advances, Ctrl+Enter
 * saves. Attach to the wrapping element (`onKeyDown={onFormKeys}`) and mark it
 * with `data-entry-form`. Buttons keep their native Enter (it clicks them);
 * textareas keep Enter as a newline; a list that handled the key first
 * (preventDefault) is left alone.
 */
export function useEntryFormKeys(onSave?: () => void) {
  return useCallback(
    (e: ReactKeyboardEvent<HTMLElement>) => {
      if (e.defaultPrevented) return;
      if (e.key !== "Enter") return;
      const t = e.target as HTMLElement;
      if ((e.ctrlKey || e.metaKey) && onSave) {
        e.preventDefault();
        onSave();
        return;
      }
      if (e.altKey || e.shiftKey) return;
      const tag = t.tagName;
      if (tag === "TEXTAREA" || tag === "BUTTON" || tag === "A" || t.getAttribute("role") === "button") return;
      if (t.getAttribute("aria-expanded") === "true") return; // an open list owns Enter
      const root = t.closest<HTMLElement>("[data-entry-form]");
      if (!root) return;
      e.preventDefault();
      focusNextField(t, root, 1);
    },
    [onSave],
  );
}

/**
 * Highlight + keys for a pick-list under a text input (typeahead, combobox).
 *   ↓ ↑ move · Home/End jump · Enter picks · Esc closes · Tab picks the
 *   highlighted row when the person typed something (then moves on as usual).
 * `count` is the number of rows; the highlight resets when it changes.
 */
export function useListNav({
  count,
  open,
  onPick,
  onClose,
  typed,
}: {
  count: number;
  open: boolean;
  onPick: (index: number) => void;
  onClose: () => void;
  /** True when the input holds text the person typed (Tab then picks). */
  typed: boolean;
}) {
  const [active, setActive] = useState(0);
  useEffect(() => { setActive(0); }, [count, open]);
  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLElement>) => {
      if (!open) {
        if (e.key === "ArrowDown" && count > 0) { e.preventDefault(); setActive(0); }
        return;
      }
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (count > 0) setActive((a) => (a + 1) % count);
          break;
        case "ArrowUp":
          e.preventDefault();
          if (count > 0) setActive((a) => (a - 1 + count) % count);
          break;
        case "Home":
          if (count > 0) { e.preventDefault(); setActive(0); }
          break;
        case "End":
          if (count > 0) { e.preventDefault(); setActive(count - 1); }
          break;
        case "Enter":
          if (count > 0) { e.preventDefault(); e.stopPropagation(); onPick(Math.min(active, count - 1)); }
          break;
        case "Tab":
          if (count > 0 && typed) onPick(Math.min(active, count - 1)); // no preventDefault: Tab still moves on
          else onClose();
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
        default:
          break;
      }
    },
    [open, count, active, onPick, onClose, typed],
  );
  return { active, setActive, onKeyDown };
}

/** Scroll the highlighted row into view inside its list. */
export function scrollActiveIntoView(list: HTMLElement | null, active: number) {
  const row = list?.querySelector<HTMLElement>(`[data-index="${active}"]`);
  row?.scrollIntoView({ block: "nearest" });
}

/**
 * A row of choice chips as ONE tab stop (the radio-group pattern): Tab lands
 * on the chosen chip, ← → (or ↑ ↓) move the choice, Space/Enter confirm.
 * Without this a row of ten lanes costs ten Tabs to get past.
 *   {keys.map((k) => <button {...radioChipProps(k, value, keys, onChange)}>…</button>)}
 */
export function radioChipProps<K extends string>(key: K, value: K, keys: readonly K[], onChange: (k: K) => void) {
  return {
    type: "button" as const,
    role: "radio" as const,
    "aria-checked": key === value,
    tabIndex: key === value ? 0 : -1,
    onClick: () => onChange(key),
    onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => {
      const i = keys.indexOf(key);
      let next: K | null = null;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") next = keys[(i + 1) % keys.length] ?? null;
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = keys[(i - 1 + keys.length) % keys.length] ?? null;
      if (e.key === "Home") next = keys[0] ?? null;
      if (e.key === "End") next = keys[keys.length - 1] ?? null;
      if (next != null) {
        e.preventDefault();
        onChange(next);
        const group = (e.currentTarget as HTMLElement).parentElement;
        window.setTimeout(() => group?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')?.focus(), 0);
      }
    },
  };
}
